// Facilitator side: every learner's canvas rows, grouped by learner and evaluated with the same engine the learners use.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { sdk } from '@/services/sdk';
import { DEFS } from '@/canvas/defs/index';
import { packReady } from '@/canvas/engine';
import { ENTRY_FIELDS, entriesFromRows, evaluateEntries } from '@/components/canvas/entries';

const PAGE = 50;
const MAX_PAGES = 20;

const ownerId = (row) => String(row?.Owner?.Id ?? row?.Owner ?? '');
const ownerName = (row) => (typeof row?.Owner === 'object' ? row.Owner?.Name || '' : '');

async function fetchAllRows() {
  const rows = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const res = await sdk
      .table('canvas_entries_c')
      .select([...ENTRY_FIELDS, 'Owner'])
      .orderBy('canvas_no_c')
      .page(page, PAGE)
      .fetch();
    if (!res.success) throw new Error(res.message || 'Could not load the cohort');
    rows.push(...(res.data || []));
    if (!res.hasMore) break;
  }
  return rows;
}

// rows -> one summary per learner, most recently active first
export function summariseLearners(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const id = ownerId(row);
    if (!id) return;
    if (!groups.has(id)) groups.set(id, { id, name: ownerName(row), rows: [] });
    const g = groups.get(id);
    if (!g.name) g.name = ownerName(row);
    g.rows.push(row);
  });
  const list = [];
  groups.forEach((g) => {
    const entries = entriesFromRows(g.rows);
    const evaluation = evaluateEntries(entries);
    const states = evaluation.states;
    const list10 = DEFS.map((d) => states[d.no]);
    const confirmed = list10.filter((s) => s?.confirmed).length;
    const overall = Math.round(list10.reduce((a, s) => a + (s?.confirmed ? 100 : s?.completion ?? 0), 0) / DEFS.length);
    const organisation = (g.rows.find((r) => r.organisation_c) || {}).organisation_c || '';
    const lastChange = g.rows.reduce((m, r) => (String(r.ModifiedOn || '') > m ? String(r.ModifiedOn) : m), '');
    list.push({
      id: g.id,
      name: g.name || 'Learner',
      organisation,
      entries,
      states,
      agg: evaluation.agg,
      confirmed,
      overall,
      ready: packReady(states),
      stale: list10.filter((s) => s?.stale).length,
      lastChange,
    });
  });
  return list.sort((a, b) => String(b.lastChange).localeCompare(String(a.lastChange)));
}

export function useCohort() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchAllRows());
    } catch (err) {
      setError(err?.message || 'Could not load the cohort');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const learners = useMemo(() => summariseLearners(rows), [rows]);
  return { learners, loading, error, reload: load };
}
